import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useQueueItems } from '../useQueueItems';
import { TaskQueueCard, EmailTaskCard } from './PriorityQueueView';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const NAV_BTN = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy-deep)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' };

function CalendarView() {
  const { items } = useQueueItems();
  const today = new Date();
  const [current, setCurrent] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [showPicker, setShowPicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(today.getFullYear());

  const year = current.getFullYear();
  const month = current.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Group items by date using dueDate field
  const itemsByDate = useMemo(() => {
    const dateMap = {};
    const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;
    items.forEach((item) => {
      const d = item.dueDate;
      if (!d || !d.startsWith(prefix)) return;
      // Extract day number from YYYY-MM-DD (handle full ISO strings too)
      const dayStr = d.slice(8, 10);
      const day = parseInt(dayStr, 10);
      if (isNaN(day) || day < 1 || day > daysInMonth) return;
      if (!dateMap[day]) dateMap[day] = [];
      dateMap[day].push(item);
    });
    return dateMap;
  }, [items, year, month, daysInMonth]);

  // Count items per day
  const countByDate = useMemo(() => {
    const map = {};
    Object.entries(itemsByDate).forEach(([day, dayItems]) => {
      map[day] = dayItems.length;
    });
    return map;
  }, [itemsByDate]);

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const selectedItems = itemsByDate[selectedDay] || [];
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const selectMonth = (mi) => {
    setCurrent(new Date(pickerYear, mi, 1));
    setShowPicker(false);
  };

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 340px) 1fr', gap: 10, padding: 8 }}>
      {/* Month grid */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Nav row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexShrink: 0 }}>
          <button type="button" onClick={() => setCurrent(new Date(year, month - 1, 1))}
            style={{ ...NAV_BTN, fontSize: 22, padding: '2px 8px' }}>{'\u2039'}</button>
          <button type="button" onClick={() => { setPickerYear(year); setShowPicker((v) => !v); }}
            style={{ ...NAV_BTN, fontSize: 12, fontWeight: 700, padding: '4px 10px', background: showPicker ? 'var(--surface)' : 'none' }}>
            {MONTHS[month]} {year}
            {showPicker
              ? <ChevronUp size={12} style={{ marginLeft: 4 }} />
              : <ChevronDown size={12} style={{ marginLeft: 4 }} />}
          </button>
          <button type="button" onClick={() => setCurrent(new Date(year, month + 1, 1))}
            style={{ ...NAV_BTN, fontSize: 22, padding: '2px 8px' }}>{'\u203A'}</button>
        </div>

        {/* Year/month picker */}
        {showPicker && (
          <div style={{ marginBottom: 8, background: 'var(--surface-warm)', borderRadius: 8, padding: '8px 10px', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
              <button type="button" onClick={() => setPickerYear((y) => y - 1)} style={{ ...NAV_BTN, fontSize: 18, padding: '2px 6px' }}>{'\u2039'}</button>
              <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-deep)', minWidth: 40, textAlign: 'center' }}>{pickerYear}</span>
              <button type="button" onClick={() => setPickerYear((y) => y + 1)} style={{ ...NAV_BTN, fontSize: 18, padding: '2px 6px' }}>{'\u203A'}</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
              {MONTHS.map((mn, mi) => (
                <button key={mn} type="button" onClick={() => selectMonth(mi)}
                  style={{
                    fontSize: 10, fontWeight: 600, padding: '5px 3px', borderRadius: 5, border: 'none', cursor: 'pointer',
                    background: mi === month && pickerYear === year ? 'var(--aqua)' : 'var(--white)',
                    color: mi === month && pickerYear === year ? 'white' : 'var(--navy-deep)',
                  }}>
                  {mn.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Calendar grid */}
        <div style={{ flex: 1, border: '1px solid var(--surface)', borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: 'var(--surface-warm)', borderBottom: '1px solid var(--surface)', flexShrink: 0 }}>
            {DAYS.map((d, di) => (
              <div key={d} style={{
                textAlign: 'center', fontSize: 9, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                color: 'var(--text-dark-secondary)', padding: '6px 0',
                borderRight: di < 6 ? '1px solid var(--surface)' : 'none',
              }}>{d}</div>
            ))}
          </div>
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gridAutoRows: '1fr' }}>
            {cells.map((day, idx) => {
              const col = idx % 7;
              const totalRows = Math.ceil(cells.length / 7);
              const row = Math.floor(idx / 7);
              const isLastRow = row === totalRows - 1;
              const cellBorder = {
                borderRight: col < 6 ? '1px solid var(--surface)' : 'none',
                borderBottom: isLastRow ? 'none' : '1px solid var(--surface)',
              };
              if (!day) return <div key={`e-${idx}`} style={{ ...cellBorder }} />;
              const count = countByDate[day] || 0;
              const isSelected = day === selectedDay;
              const isToday = isCurrentMonth && day === today.getDate();
              return (
                <button key={day} type="button" onClick={() => setSelectedDay(day)}
                  style={{
                    padding: '4px 5px', position: 'relative',
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                    background: isSelected ? 'var(--aqua-glow)' : 'transparent',
                    cursor: 'pointer', outline: 'none',
                    border: 'none', ...cellBorder,
                  }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: '50%', fontSize: 11, lineHeight: 1,
                    fontWeight: isToday || isSelected ? 700 : 400,
                    background: isSelected ? 'var(--aqua)' : 'transparent',
                    color: isSelected ? 'white' : isToday ? 'var(--aqua)' : 'var(--navy-deep)',
                    boxShadow: isToday && !isSelected ? '0 0 0 1.5px var(--aqua)' : 'none',
                  }}>{day}</span>
                  {count > 0 && (
                    <span style={{
                      position: 'absolute', bottom: 3, right: 5,
                      fontSize: 11, fontWeight: 700, lineHeight: 1,
                      color: isSelected ? 'var(--aqua)' : 'var(--text-muted)',
                    }}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Day detail sidebar — shows TaskQueueCards */}
      <div style={{ background: 'var(--surface-warm)', borderRadius: 8, padding: '10px 12px', overflowY: 'auto', minHeight: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-deep)', marginBottom: 10 }}>
          {MONTHS[month]} {selectedDay}
          <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8, fontSize: 11 }}>
            {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''}
          </span>
        </div>
        {selectedItems.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>No items scheduled.</div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {selectedItems.map((item) => {
              const isEmailTopic = item.type === 'email_topic' || item.threadContext?.sourceType === 'email_topic';
              const Card = isEmailTopic ? EmailTaskCard : TaskQueueCard;
              return <Card key={item.id} item={item} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export { CalendarView };
